import React from 'react';
import { DiabetesPageTemplate } from './DiabetesPageTemplate';
import { diabetesImages } from './diabetesPageData';

const Type2Diabetes = () => {
  return (
    <DiabetesPageTemplate
      eyebrow="Type 2 Diabetes"
      title="Type 2 Diabetes"
      intro="Type 2 diabetes is the most common form of diabetes and is strongly linked to insulin resistance. It usually develops slowly over time and may remain unnoticed until complications begin to appear."
      heroSummary="With early lifestyle changes, regular monitoring, and the right medicines, many people can manage Type 2 diabetes effectively."
      heroImage={diabetesImages.img2}
      heroAlt="Type 2 diabetes awareness and lifestyle care"
      sections={[
        {
          title: 'What Is Type 2 Diabetes?',
          paragraphs: [
            'Type 2 diabetes occurs when the pancreas cannot produce enough insulin to regulate blood sugar, or the body cannot use insulin effectively (insulin resistance). It develops slowly over years, often without noticeable symptoms. Before diagnosis, many people have pre-diabetes, a stage where lifestyle changes can prevent progression.',
            'Unlike Type 1, Type 2 is most common in middle-aged and older adults, especially those who are overweight or physically inactive. It accounts for 85 to 95% of all diabetes cases. Insulin resistance is the core issue, often marked by darkened, velvety skin on the neck and armpits.',
            'Over time, uncontrolled high blood sugar can lead to serious complications such as blindness, kidney failure, heart attack, and amputation. However, with proper lifestyle changes, medication, and monitoring, these risks can be significantly reduced.',
          ],
          image: diabetesImages.checkupImg,
          imageAlt: 'Type 2 diabetes screening and monitoring',
          imageCaption: 'Because Type 2 diabetes progresses slowly, screening and routine check-ups are especially important.',
          note: 'Type 2 diabetes can sometimes be reversed or put into remission with sustained lifestyle changes, especially weight loss and increased physical activity.',
        },
        {
          title: 'Causes And Risk Factors',
          paragraphs: [
            'Multiple factors contribute to Type 2 diabetes. While genetics play a role, modifiable lifestyle factors are key.',
            'Excess belly fat releases hormones that promote insulin resistance. The body initially compensates by producing more insulin, but over time, the pancreas cannot keep up, leading to prediabetes and eventually diabetes.',
          ],
          columns: [
            {
              title: 'Non-Modifiable',
              items: [
                'Family history as close relatives with Type 2 diabetes increase risk significantly',
                'Age, especially after 45 years',
                'Ethnicity, as certain groups are at higher risk',
              ],
            },
            {
              title: 'Modifiable',
              items: [
                'Overweight or obesity, especially excess belly fat',
                'Physical inactivity or sedentary lifestyle',
                'Unhealthy diet high in processed foods and sugars',
                'Smoking and excessive alcohol intake',
              ],
            },
          ],
        },
        {
          title: 'Symptoms Of Type 2 Diabetes',
          paragraphs: [
            'Symptoms are often mild and develop slowly, so many people do not notice them. However, common signs include:',
          ],
          columns: [
            {
              title: 'Common Symptoms',
              items: [
                'Increased appetite because cells lack energy despite high blood sugar',
                'Increased thirst and frequent urination',
                'Fatigue and tiredness',
                'Headache and irritability',
                'Unexplained weight loss',
                'Itching or infections around genitals',
                'Slow wound healing',
                'Blurred vision',
              ],
            },
            {
              title: 'Emergency Symptoms',
              items: [
                'Fruity breath',
                'Troubled breathing',
                'Vomiting and stomach pain',
                'Increased heart rate and weakness',
                'Altered consciousness or seizures',
              ],
            },
          ],
          image: diabetesImages.heroAltMain,
          imageAlt: 'Diabetes symptom overview',
          imageCaption: 'Symptoms may remain mild for a long time, which is why many people discover Type 2 diabetes late.',
          note: 'A unique sign of insulin resistance can be darkened, velvety skin on the neck, armpits, or other body folds. Because symptoms can stay silent, everyone at risk should get tested regularly even if they feel fine.',
        },
        {
          title: 'Treating Type 2 Diabetes',
          paragraphs: [
            'While Type 2 diabetes cannot be cured, it can be managed effectively. Treatment focuses on bringing blood sugar close to normal to prevent complications.',
          ],
          columns: [
            {
              title: 'Healthy Diet',
              text: 'Balanced meals with controlled carbohydrates, fiber-rich foods, lean proteins, and healthy fats.',
            },
            {
              title: 'Physical Activity',
              text: 'Aim for 150 minutes of moderate exercise such as brisk walking, or 75 minutes of vigorous activity per week, as approved by your doctor.',
            },
            {
              title: 'Medications',
              text: 'Oral anti-diabetic drugs and or insulin depending on individual needs, coexisting conditions, and risk of hypoglycemia.',
            },
            {
              title: 'Self-Monitoring',
              text: 'Regular blood glucose checks help you and your doctor adjust treatment in real time.',
            },
          ],
          items: [
            'Manage blood pressure and cholesterol.',
            'Quit smoking and reduce alcohol.',
            'Practice regular foot care.',
            'Get regular check-ups at a diabetes centre because treatment needs may change over time.',
          ],
        },
        {
          title: 'Monitoring Targets',
          paragraphs: [
            'Keeping your blood sugar, lipids, and blood pressure within target ranges is vital. Discuss your personal goals with your physician.',
          ],
          items: [
            'HDL cholesterol: Men more than 40 mg/dL; women more than 50 mg/dL',
            'LDL cholesterol: As discussed with your physician, usually less than 100 mg/dL',
            'VLDL: Individualized goal',
            'Blood pressure: Often less than 130/80 mm Hg or as discussed with your physician',
            'Regular HbA1c tests, usually every 3 to 6 months, along with kidney tests, eye exams, and foot checks',
          ],
        },
      ]}
    />
  );
};

export default Type2Diabetes;
